import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { partnersPageFooter } from './layout/footerConfigs'
import { SEOHead } from './SEO/SEOHead'

export const PartnersPage: React.FC = () => {
  const isMobile = useIsMobile()
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' 
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const { scrollProgress } = useOptimizedScroll()

  return (
    <>
      {/* SEO Head */}
      <SEOHead 
        customTitle="Partners - Mernaki"
        customDescription="Partner with Mernaki across software, hardware, and enterprise technology. Join our network of technology partners, compliance experts, R&D innovators, and creative collaborators."
      />

      <motion.div 
        className="min-h-screen relative"
        style={{ 
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: '48px',
          borderBottomRightRadius: '48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transform: `translateY(${scrollProgress * -20}px)`
        }}
        transition={{ duration: 0 }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div 
          className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none" 
          style={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            top: '15%',
            left: '8%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, -80, 60, 0],
            y: [0, 70, -50, 0],
            scale: [1, 1.3, 0.85, 1]
          }}
          transition={{ duration: 27, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div 
          className="absolute w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none" 
          style={{ 
            background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
            top: '50%',
            right: '5%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 90, -70, 0],
            y: [0, -60, 80, 0],
            scale: [1, 0.9, 1.2, 1]
          }}
          transition={{ duration: 32, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div 
          className="absolute w-72 h-72 rounded-full opacity-15 blur-3xl pointer-events-none" 
          style={{ 
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            bottom: '15%',
            left: '12%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 70, -90, 0],
            y: [0, -80, 50, 0],
            scale: [1, 1.15, 0.9, 1]
          }}
          transition={{ duration: 29, repeat: Infinity, ease: 'easeInOut' }}
        />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} />
        <WhatWeLookForSection prefersReducedMotion={prefersReducedMotion.current} />
        <WhyPartnerSection prefersReducedMotion={prefersReducedMotion.current} />
        <BecomePartnerSection prefersReducedMotion={prefersReducedMotion.current} />
        <RoadmapSection prefersReducedMotion={prefersReducedMotion.current} />
        <ContactSection prefersReducedMotion={prefersReducedMotion.current} />
      </motion.div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}

const HeroSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  
  return (
    <section ref={ref} className="relative pt-56 pb-32 overflow-hidden" style={{ backgroundColor: 'transparent' }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1 
          className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Partner With Mernaki
        </motion.h1>
        
        <motion.p 
          className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed mb-4" 
          style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Building the future of software, hardware, and enterprise technology — together.
        </motion.p>
        
        <motion.p 
          className="text-base md:text-lg max-w-3xl mx-auto leading-relaxed mb-12" 
          style={{ color: '#999999', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Mernaki partners with companies, creators, suppliers, and organizations across multiple industries. From recruitment technology to enterprise-grade software, from hardware R&D to compliance systems — we collaborate with teams who share our passion for innovation, design excellence, and long-term value.
        </motion.p>
        
        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <Link to="/partnerships">
            <motion.button
              className="px-8 py-4 rounded-xl text-base"
              style={{
                backgroundColor: '#1A1A1A',
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer'
              }}
              whileHover={prefersReducedMotion ? {} : { 
                scale: 1.05,
                y: -2,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
              }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
              transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333333'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1A1A1A'}
            >
              Become a Partner
            </motion.button>
          </Link>
          <Link to="/contact">
            <motion.button
              className="px-8 py-4 rounded-xl text-base"
              style={{
                backgroundColor: 'transparent',
                color: '#1A1A1A',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                border: '2px solid #1A1A1A',
                cursor: 'pointer'
              }}
              whileHover={prefersReducedMotion ? {} : { 
                scale: 1.05,
                y: -2,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
              }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
              transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1A1A1A'
                e.currentTarget.style.color = '#FFFFFF'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.color = '#1A1A1A'
              }}
            >
              Request a Call
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

const WhatWeLookForSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  
  const partnerTypes = [
    {
      icon: '🟦',
      title: 'Technology Partners',
      description: 'Software companies, SaaS platforms, AI tools, cloud providers — anyone who integrates with Mernaki products.'
    },
    {
      icon: '🟩',
      title: 'Enterprise & Compliance Partners',
      description: 'Consultants, verification bodies, compliance auditors, CBAM specialists, data service providers.'
    },
    {
      icon: '🟨',
      title: 'Hardware & R&D Partners',
      description: 'Component manufacturers, industrial designers, electronics suppliers, product labs, prototyping facilities.'
    },
    {
      icon: '🟧',
      title: 'Creative & Innovation Partners',
      description: 'Design studios, creators, content teams, 3D/UE5 developers, and companies exploring next-generation experiences.'
    },
    {
      icon: '🟥',
      title: 'Recruitment & HR Partners',
      description: 'Agencies, HR software vendors, ATS systems, recruiters, job boards.'
    }
  ]
  
  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl mb-16 text-center" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Partnership Categories
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {partnerTypes.map((type, index) => (
            <motion.div
              key={index}
              className="p-8 rounded-2xl transition-all duration-300"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)'
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
            >
              <div className="text-3xl mb-4">{type.icon}</div>
              <h3 
                className="text-xl mb-3" 
                style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
              >
                {type.title}
              </h3>
              <p 
                className="text-sm leading-relaxed" 
                style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}
              >
                {type.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const WhyPartnerSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  
  const benefits = [
    {
      title: 'Modern Product Ecosystem',
      description: 'Mernaki builds software, hardware concepts, enterprise tools, and digital platforms that power multiple industries.'
    },
    {
      title: 'High-End Design & Engineering Standards',
      description: 'Every product follows a premium, minimal, future-focused design philosophy.'
    },
    {
      title: 'Scalable Collaboration',
      description: 'Partnerships can span HR, AI, hardware research, compliance, and commercial services — all under one brand.'
    },
    {
      title: 'Global Vision',
      description: 'We work with companies in Europe, Asia, Africa, and the US on projects of all sizes.'
    }
  ]
  
  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl mb-16 text-center" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Why Partner With Mernaki
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="p-8 rounded-2xl transition-all duration-300"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)'
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
            >
              <h3 
                className="text-xl mb-3" 
                style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
              >
                {benefit.title}
              </h3>
              <p 
                className="text-base leading-relaxed" 
                style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}
              >
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const BecomePartnerSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  
  const steps = [
    { number: '1', text: 'Fill out the partnership request form' },
    { number: '2', text: 'Our team reviews your application' },
    { number: '3', text: 'We schedule a call to explore opportunities' }
  ]
  
  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="p-12 rounded-3xl"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 
            className="text-3xl md:text-4xl mb-4 text-center" 
            style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
          >
            How to Get Started
          </h2>
          <p 
            className="text-lg mb-12 text-center leading-relaxed" 
            style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}
          >
            Join our growing network of partners in three simple steps
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
              >
                <div className="mb-6">
                  <span
                    className="text-5xl md:text-6xl block mb-4"
                    style={{
                      background: 'linear-gradient(135deg, #1A1A1A 0%, rgba(26, 26, 26, 0.2) 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 800,
                      letterSpacing: '-0.05em',
                      lineHeight: 1
                    }}
                  >
                    {step.number}
                  </span>
                </div>
                <p 
                  className="text-base"
                  style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }}
                >
                  {step.text}
                </p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/partnerships">
              <motion.button
                className="px-8 py-4 rounded-xl text-base"
                style={{
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer'
                }}
                whileHover={prefersReducedMotion ? {} : { 
                  scale: 1.05,
                  y: -2,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
                }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333333'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1A1A1A'}
              >
                Start Partnership Request
              </motion.button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

const RoadmapSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  
  const models = [
    {
      title: 'Integration Partnerships',
      description: 'API connections, shared features, cross-platform integrations.'
    },
    {
      title: 'Commercial Partnerships',
      description: 'Supply chain, rare earth materials, CBAM verification, enterprise deals.'
    },
    {
      title: 'Development Partnerships',
      description: 'Hardware testing, prototyping, software co-development.',
      hasBlurred: true,
      blurredText: '(CodeRED IDE, MacX Launcher, BeyondReal tools)'
    },
    {
      title: 'Innovation Partnerships',
      description: 'Joint R&D projects, design collaborations, early access programs.'
    }
  ]
  
  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl mb-16 text-center" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Partnership Models
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {models.map((model, index) => (
            <motion.div
              key={index}
              className="p-8 rounded-2xl transition-all duration-300"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)'
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
            >
              <h3 
                className="text-xl mb-3" 
                style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
              >
                {model.title}
              </h3>
              <p 
                className="text-base leading-relaxed" 
                style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}
              >
                {model.description}
                {model.hasBlurred && (
                  <span 
                    style={{ 
                      filter: 'blur(3px)', 
                      userSelect: 'none', 
                      pointerEvents: 'none',
                      marginLeft: '4px'
                    }}
                  >
                    {model.blurredText}
                  </span>
                )}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const ContactSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  
  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          className="p-12 rounded-3xl"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 
            className="text-3xl md:text-4xl mb-4" 
            style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
          >
            Have questions?
          </h2>
          <p 
            className="text-lg mb-8" 
            style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}
          >
            Let's talk partnership opportunities.
          </p>
          
          <Link to="/contact">
            <motion.button
              className="px-8 py-4 rounded-xl text-base"
              style={{
                backgroundColor: '#1A1A1A',
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer'
              }}
              whileHover={prefersReducedMotion ? {} : { 
                scale: 1.05,
                y: -2,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
              }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
              transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333333'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1A1A1A'}
            >
              Contact Us
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  
  if (isMobile) {
    return <PageMobileFooter {...partnersPageFooter} />
  }
  
  return (
    <footer ref={ref} className="relative" style={{ backgroundColor: '#0E0E0E', position: 'sticky', bottom: 0, zIndex: 1, paddingTop: '140px', marginTop: '-80px' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-16">
          {/* Footer Header */}
          <motion.div
            className="mb-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <h2 
              className="text-3xl md:text-4xl mb-4"
              style={{ 
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                letterSpacing: '-0.02em'
              }}
            >
              Partner With Us
            </h2>
            <p 
              className="text-base md:text-lg max-w-2xl mx-auto"
              style={{ 
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400,
                lineHeight: '1.6'
              }}
            >
              Join our growing network of partners and help shape the future of recruitment.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
          >
            {/* Column 1 - Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                  alt="Mernaki Logo"
                  className="object-contain"
                  style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                  loading="lazy"
                />
                <h3 
                  className="text-lg"
                  style={{ 
                    color: '#FFFFFF',
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                    fontWeight: 400
                  }}
                >
                  Mernaki
                </h3>
              </div>
              <p 
                className="text-sm leading-relaxed"
                style={{ 
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400
                }}
              >
                Partner with us to shape the future of recruitment.
              </p>
            </div>

            {/* Column 2 - Company */}
            <div>
              <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Company
              </h3>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>About</Link></li>
                <li><Link to="/team" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Team</Link></li>
                <li><Link to="/partnerships" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Partnerships</Link></li>
                <li><Link to="/contact" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Contact</Link></li>
              </ul>
            </div>

            {/* Column 3 - Support */}
            <div>
              <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Support
              </h3>
              <ul className="space-y-3">
                <li><Link to="/help" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Help Center</Link></li>
                <li><Link to="/faq" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>FAQ</Link></li>
              </ul>
            </div>

            {/* Column 4 - Legal */}
            <div>
              <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Legal
              </h3>
              <ul className="space-y-3">
                <li><Link to="/privacy-policy" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Terms of Service</Link></li>
                <li><Link to="/impressum" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Impressum</Link></li>
              </ul>
            </div>
          </motion.div>

          {/* Showcase Row */}
          <div className="mb-10 pb-10 border-b border-white/10">
            <div className="flex items-center justify-center gap-3 flex-wrap text-sm">
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Project Crimson</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Project Velocity</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Creator Tools</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Project Nexus</span>
            </div>
          </div>

          {/* Bottom Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center"
          >
            <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 300 }}>© {new Date().getFullYear()} Mernaki • Built in Germany with passion</p>
          </motion.div>
        </div>
      </div>
    </footer>
  )
}
